import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from './store';
import { logout } from './counterSlice';
import { LANGUAGE_VERSIONS } from '../constants';

const API_URL = process.env.REACT_APP_API_URL;

const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState, endpoint }) => {
    const state = getState() as RootState;
    const accessToken = state.app.user.accessToken;

    const requiresAuth = [
      'getUserProfile',
      'getUserSubscription',
      'cancelUserSubscription',
      'resetPassword',
      'emailVerify',
      'updateEmail',
      'getAllCourses',
      'restartUserSubscription',
      'logoutUser',
      'getCourseDetails',
      'getNextCourseDetails',
      'getCourseTopics',
      'getSectionDetails',
      'getSectionQuestions',
      'submitCourseAttempt',
      'trackCourseProgress',
      'aiCodeChecker',
      'enrollCourse',
      'completeEnrollCourse',
      'retakeCourse',
      'saveWeeklyGoal',
      'getWeeklyGoal',
      'getCourseLanguageDistribution',
      'getDashboardGraph',
      'getCourseMedals',
      'getCourseReport',
      'getCourseDetailedReport',
      'getAllCourseQuestions',
      'getSurveyQuestions',
      'submitSurveyResponse',
      'getBlogList',
      'getBlogDetails',
      'pingUserActivity',
    ].includes(endpoint);

    if (requiresAuth && accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    headers.set('ngrok-skip-browser-warning', '69420');
    return headers;
  },
});

const baseQueryWithLogout = async (args: any, api: any, extraOptions: any) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    api.dispatch(logout());
  }

  return result;
};

const executeCodeBaseQuery = fetchBaseQuery({
  baseUrl: 'https://emkc.org/api/v2/piston',
});

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithLogout,
  endpoints: (builder) => ({
    logoutUser: builder.mutation<void, void>({
      query: () => ({
        url: '/user/logout/',
        method: 'POST',
      }),
    }),

    registerUser: builder.mutation<
      { user?: any },
      {
        studentFirstName: string;
        parentsEmail: string;
        studentsAge: string;
        studentsGender: string;
        subscriptionType: string;
        promoCode?: string;
        referralCode: string;
        userReferralCode?: string;
        couponCode?: string;
        traffic_source_of_initial_conversion?: string;
        traffic_source_details?: string;
      }
    >({
      query: (userData) => {
        const transformedData = {
          name: userData.studentFirstName,
          email: userData.parentsEmail.toLowerCase(),
          age: userData.studentsAge,
          gender: userData.studentsGender,
          subscription: userData.subscriptionType,
          traffic_source_of_initial_conversion:
            userData.traffic_source_of_initial_conversion,
          traffic_source_details: userData.traffic_source_details || '',
          promo_code: userData.promoCode,
          referral_code: userData.referralCode,
          user_referral_code: userData.userReferralCode || '',
        };

        return {
          url: '/user/manager/',
          method: 'POST',
          body: transformedData,
        };
      },
    }),

    submitAffiliateForm: builder.mutation<
      void,
      {
        first_name: string;
        last_name: string;
        country_code: string | undefined | null;
        country: string;
        city: string;
        postal_code: number | null;
        address: string;
        email: string;
        expertise: string[];
        platforms: string[];
        website_url: string;
        linked_in: string;
        facebook?: string;
        instagram?: string;
        twitter?: string;
        youtube?: string;
        audience_size?: number | null;
        referral_source?: string;
        business: string;
        dob?: string;
      }
    >({
      query: (formData) => ({
        url: '/affiliate/',
        method: 'POST',
        body: formData,
      }),
    }),

    // Get user profile - requires auth token
    getUserProfile: builder.query<
      {
        name: string;
        email: string;
        decrypted_password: string;
        age: string;
        gender: string;
        subscription: string;
        referral_code: string;
        created_at: string;
      },
      void
    >({
      query: () => ({
        url: '/user/manager/',
        method: 'GET',
      }),
    }),
    getUserSubscription: builder.query<
      {
        id: number;
        current_plan_starting_date: string | null;
        current_plan_ending_date: string | null;
        plan: string;
        active: boolean;
        is_plan_cancelled: boolean;
      },
      void
    >({
      query: () => ({
        url: '/subscription/subscription-manager/',
        method: 'GET',
      }),
    }),
    // Cancel the user Subscription
    cancelUserSubscription: builder.mutation<
      { refresh: string; access: string },
      { user_id: number; cancel_reason: string }
    >({
      query: (credentials) => ({
        url: '/subscription/cancel-subscription/',
        method: 'POST',
        body: credentials,
      }),
    }),

    // Verify user endpoint
    verifyUser: builder.mutation<
      {
        subscription: any;
        user: any;
        success: boolean;
        password: string;
        message: string;
      },
      { userIdAsNumber: number; username: string }
    >({
      query: ({ userIdAsNumber, username }) => ({
        url: `/user/verify?userId=${encodeURIComponent(userIdAsNumber)}&username=${encodeURIComponent(username)}`,
        method: 'GET',
      }),
    }),

    // Login user mutation
    loginUser: builder.mutation<
      {
        refresh: string;
        access: string;
        user_info: any;
        survey_required: boolean;
        survey_id: number;
      },
      { email: string; password: string; token?: string; user_device?: string }
    >({
      query: (credentials) => ({
        url: '/user/login/',
        method: 'POST',
        body: credentials,
      }),
    }),

    // Forgot password mutation
    forgetPassword: builder.mutation<void, { email: string }>({
      query: (credentials) => ({
        url: `/user/forget-password?email=${encodeURIComponent(credentials.email)}`,
        method: 'GET',
      }),
    }),

    emailVerify: builder.mutation<
      void,
      {
        newMail: string;
      }
    >({
      query: (userData) => {
        const transformedData = {
          newMail: userData.newMail,
        };

        return {
          url: 'user/email-verify/',
          method: 'POST',
          body: transformedData,
        };
      },
    }),

    updateEmail: builder.mutation<
      { success: boolean; message: string },
      { email: string }
    >({
      query: (userData) => {
        const transformedData = {
          email: userData.email,
        };

        return {
          url: 'user/email-verify/',
          method: 'PUT',
          body: transformedData,
        };
      },
    }),

    // Reset password mutation (requires auth token)
    resetPassword: builder.mutation<
      void,
      { oldPassword: string; newPassword: string }
    >({
      query: (credentials) => ({
        url: '/user/reset-password/',
        method: 'POST',
        body: credentials,
      }),
    }),

    // Submit course attempt
    submitCourseAttempt: builder.mutation<
      {
        id: number;
        attempted_code: string;
        words_per_minute: string;
        is_correct: boolean;
        debug_help_count: boolean;
        explain_help_count: boolean;
        is_completed: boolean;
        attempt_count: number;
        question: number;
        user: number;
      },
      {
        user: number;
        question: number;
        attempted_code: string;
        words_per_minute: number;
        is_correct: boolean;
        is_completed: boolean;
        debug_help_count: boolean;
        explain_help_count: boolean;
      }
    >({
      query: (attemptData) => ({
        url: '/course/attempt/',
        method: 'POST',
        body: attemptData,
      }),
    }),

    getAllCourses: builder.query<
      {
        courses: {
          id: number;
          language: {
            id: number;
            name: string;
          };
          level: {
            id: number;
            name: string;
          };
          detail?: {
            course_completed: boolean;
            highest_medal: string;
            total_questions: number;
            total_correct_questions: number;
            time_spent: number;
            last_incomplete_question_id: number;
            last_incomplete_section_id: number;
            last_incomplete_question_attempts: number;
          };
          course_id: number;
          course_name: string;
          duration: string;
          objective: string;
          total_activity_number: number;
        }[];
      },
      void
    >({
      query: () => ({
        url: '/course',
        method: 'GET',
      }),
    }),
    executeCode: builder.mutation<
      { run: { output: string; stderr?: string } },
      { language: string; sourceCode: string }
    >({
      query: ({ language, sourceCode }) => ({
        url: 'http://172.18.0.6:5000/run', // Custom URL for this endpoint
        method: 'POST',
        body: {
          language,
          version:
            LANGUAGE_VERSIONS[language as keyof typeof LANGUAGE_VERSIONS],
          files: [{ content: sourceCode }],
        },
      }),
    }),
    fetchCountries: builder.query<
      { id: number; name: string; code: string }[],
      void
    >({
      query: () => ({
        url: '/affiliate/country/',
        method: 'GET',
      }),
    }),
    fetchStates: builder.query<
      { id: number; name: string; code: string; country: number }[],
      string
    >({
      query: (countryId) => ({
        url: `/affiliate/state/?country=${countryId}`,
        method: 'GET',
      }),
    }),
    fetchCities: builder.query<
      { id: number; name: string; code: string; state: number }[],
      string
    >({
      query: (stateId) => ({
        url: `/affiliate/city/?state=${stateId}`,
        method: 'GET',
      }),
    }),

    getW9TaxForm: builder.mutation<
      {
        widget_url: string;
      },
      {
        affiliateId: string;
        email: string;
        firstName: string;
        street1: string;
        street2: string;
        city?: string;
        postalCode?: number | null;
        region?: string;
        country?: string;
      } // Updated type to accept params
    >({
      query: ({
        affiliateId,
        email,
        firstName,
        street1,
        street2,
        city,
        postalCode,
        region,
        country,
      }) => ({
        url: `/affiliate/getTaxForm/?refid=${affiliateId}&email=${email}&firstName=${firstName}&street1=${street1}&street2=${street2}&city=${city}&postalCode=${postalCode}&region=${region}&country=${country}`,
        method: 'GET',
      }),
    }),

    checkPromoCode: builder.mutation<
      void,
      { code: string; subscriptionType: '3 days Trial' | '1-year' | '2-year' }
    >({
      query: (credentials) => ({
        url: '//', // give here apply coupon api
        method: 'POST',
        body: credentials,
      }),
    }),

    submitTaxForm: builder.mutation<
      void,
      {
        name: string;
        businessName: string;
        address: string;
        country: string;
        city: string;
        state: string;
        zipCode: string;
        taxPayerIdentificationNumber: string;
        federalTaxClassification: string;
      }
    >({
      query: (formData) => ({
        url: '//', // use form submit api here
        method: 'POST',
        body: formData,
      }),
    }),
    createSetupIntent: builder.mutation<
      void,
      {
        user: string;
        paymentMethodId: string | undefined;
        email: string;
        name: string;
        subscriptionType?: string;
      }
    >({
      query: ({ email, name, user, paymentMethodId, subscriptionType }) => ({
        url: '/subscription/create-setupintent/', // use form submit api here
        method: 'POST',
        body: { email, name, user, paymentMethodId, subscriptionType },
      }),
    }),
    // createSetupIntent: builder.mutation<
    //   void,
    //   {
    //     user: string;
    //   }
    // >({
    //   query: ({ user }) => ({
    //     url: '/subscription/create-setupintent/', // use form submit api here
    //     method: 'POST',
    //     body: { user },
    //   }),
    // }),
    activeFreeTrial: builder.mutation<
      void,
      {
        user: string;
        subscription_id: string;
      }
    >({
      query: ({ user, subscription_id }) => ({
        url: '/acive-free-trial-subscription/', // use form submit api here
        method: 'POST',
        body: { user, subscription_id },
      }),
    }),
    restartUserSubscription: builder.mutation<
      { success: boolean; message: string },
      { user_id: number }
    >({
      query: (credentials) => ({
        url: '/subscription/restart-subscription/',
        method: 'POST',
        body: credentials,
      }),
    }),
    sendSecurityEmail: builder.mutation<
      { success: boolean; message: string },
      string
    >({
      query: (email) => ({
        url: `/user/recover/session?email=${email}`,
        method: 'GET',
      }),
    }),

    // Get course details
    getCourseDetails: builder.query<
      {
        id: number;
        language: {
          id: number;
          name: string;
        };
        best_feedback: string;
        feedback: string;
        level: {
          id: number;
          name: string;
        };
        detail?: {
          is_correct: boolean;
          total_questions: number;
          total_correct_questions: number;
          time_spent: number | null;
          last_incomplete_question_id: number;
          last_incomplete_section_id: number;
          last_incomplete_question_attempts: number;
          course_completed: boolean;
          highest_medal: string;
        };
        created_at: string;
        updated_at: string;
        is_active: boolean;
        course_id: number;
        course_name: string;
        duration: string;
        objective: string;
        total_activity_number: number;
      },
      number
    >({
      query: (courseId) => ({
        url: `/course/${courseId}/`,
        method: 'GET',
      }),
    }),

    // Get course details
    getNextCourseDetails: builder.query<
      {
        id: number;
        language: {
          id: number;
          name: string;
        };
        best_feedback: string;
        feedback: string;
        level: {
          id: number;
          name: string;
        };
        detail?: {
          total_questions: number;
          total_correct_questions: number;
          time_spent: number | null;
          last_incomplete_question_id: number;
          last_incomplete_section_id: number;
          last_incomplete_question_attempts: number;
          course_completed: boolean;
          highest_medal: string;
        };
        created_at: string;
        updated_at: string;
        is_active: boolean;
        course_id: number;
        course_name: string;
        duration: string;
        objective: string;
        total_activity_number: number;
      },
      number
    >({
      query: (courseId) => ({
        url: `/course/nextcourse/${courseId}/`,
        method: 'GET',
      }),
    }),

    // Get course topics/sections
    getCourseTopics: builder.query<
      {
        id: number;
        created_at: string;
        updated_at: string;
        is_active: boolean;
        section_id: number;
        title: string;
        description: string;
        order: number;
        estimated_duration: string;
        course: number;
      }[],
      number
    >({
      query: (courseId) => ({
        url: `/course/${courseId}/section/`,
        method: 'GET',
      }),
    }),

    // Get section details
    getSectionDetails: builder.query<
      {
        id: number;
        created_at: string;
        updated_at: string;
        is_active: boolean;
        section_id: number;
        title: string;
        description: string;
        order: number;
        estimated_duration: string;
        course: number;
      },
      { courseId: number; sectionId: number }
    >({
      query: ({ courseId, sectionId }) => ({
        url: `/course/${courseId}/section/${sectionId}/`,
        method: 'GET',
      }),
    }),

    // Get section questions
    getSectionQuestions: builder.query<
      {
        id: number;
        created_at: string;
        updated_at: string;
        is_active: boolean;
        question_id: number;
        prompt: string;
        provided_code: string;
        explanation: string;
        tip: string;
        expected_output: string;
        is_typing_course: boolean;
        ai_validation_required: boolean;
        order_in_section: number;
        is_final_activity: boolean;
        section: number;
        course: number;
        compare_mode: number;
        detail?: {
          attempted_code?: string;
          words_per_minute?: number;
          attempt_count?: number;
          ai_usage?: boolean;
        };
      }[],
      { courseId: number; sectionId: number }
    >({
      query: ({ courseId, sectionId }) => ({
        url: `/course/${courseId}/section/${sectionId}/question/`,
        method: 'GET',
      }),
    }),

    trackCourseProgress: builder.mutation({
      query: (payload) => ({
        url: '/dashboard/tracking/',
        method: 'POST',
        body: payload,
      }),
    }),

    // Enroll course
    completeEnrollCourse: builder.mutation<
      void,
      { course: number; is_completed: boolean }
    >({
      query: (payload) => ({
        url: '/course/enroll-course/',
        method: 'PUT',
        body: payload,
      }),
    }),

    // Enroll course
    enrollCourse: builder.mutation<void, { course: number }>({
      query: (payload) => ({
        url: '/course/enroll-course/',
        method: 'POST',
        body: payload,
      }),
    }),

    // AI Code Checker
    aiCodeChecker: builder.mutation<
      { response: string },
      { prompt: string; used_for: string; language: string }
    >({
      query: (data) => ({
        url: '/ai/checker/',
        method: 'POST',
        body: data,
      }),
    }),

    // Retake Course
    retakeCourse: builder.mutation<string, number>({
      query: (courseId) => ({
        url: `/course/retake?course=${courseId}`,
        method: 'GET',
      }),
    }),

    // Save weekly goal
    saveWeeklyGoal: builder.mutation<
      { id: number; target: string; user: number },
      { target: number }
    >({
      query: (payload) => ({
        url: '/dashboard/target/',
        method: 'POST',
        body: payload,
      }),
    }),

    // Get weekly goal and progress
    getWeeklyGoal: builder.query<
      { total_time_spent: number | null; target_time: number },
      void
    >({
      query: () => ({
        url: '/dashboard/target/',
        method: 'GET',
      }),
    }),

    // Get course language distribution
    getCourseLanguageDistribution: builder.query<
      { data: Array<{ language__name: string; course_count: number }> },
      void
    >({
      query: () => ({
        url: '/dashboard/course/',
        method: 'GET',
      }),
    }),

    getDashboardGraph: builder.query<
      {
        language_level_graph: Record<
          string,
          {
            by_language: Record<string, number>;
            by_level: Record<string, number>;
          }
        >;
        medal_count_graph: Record<
          string,
          {
            bronze?: number;
            gold?: number;
            silver?: number;
          }
        >;
        date_ranges: Array<[string, string]>;
      },
      { interval: 'weekly' | 'monthly' | 'annually' }
    >({
      query: ({ interval }) => ({
        url: `/dashboard/graph/?interval=${interval}`,
        method: 'GET',
      }),
    }),

    getCourseMedals: builder.query<
      Array<{
        id: number;
        medal: string;
        completed_in: number;
        attempt_count: number;
        feedback: string;
      }>,
      number
    >({
      query: (courseId) => ({
        url: `/course/medal?course=${courseId}`,
        method: 'GET',
      }),
    }),

    // Get course report
    getCourseReport: builder.query<
      {
        course: number;
        medal: string;
        feedback: string;
      },
      number
    >({
      query: (courseId) => ({
        url: `/course/report?course=${courseId}`,
        method: 'GET',
      }),
    }),

    // Get detailed course report with sections data
    getCourseDetailedReport: builder.query<
      {
        error_rate: Record<string, { name: string; error_rate: number }> & {
          overall_rate: number;
        };
        ai_usage_rate: Record<
          string,
          { name: string; ai_usage_rate: number }
        > & { overall_ai_rate: number };
        medal_criteria: {
          gold: {
            error_rate: string;
            ai_usage_rate: string;
          };
          silver: {
            error_rate: string;
            ai_usage_rate: string;
          };
          bronze: {
            error_rate: string;
            ai_usage_rate: string;
          };
        };
        medal: string;
        personalized_feedback: string | null;
      },
      number
    >({
      query: (courseId) => ({
        url: `/course/report?course=${courseId}`,
        method: 'POST',
        body: { course: courseId },
      }),
    }),

    // Get survey questions
    getSurveyQuestions: builder.query<
      {
        id: number;
        name: string;
        message: string;
        duration: string;
        questions: Array<{
          id: number;
          question: string;
          options: string;
        }>;
      },
      number
    >({
      query: (surveyId) => ({
        url: `/survey/types/${surveyId}`,
        method: 'GET',
      }),
    }),

    // Submit survey response
    submitSurveyResponse: builder.mutation<
      void,
      { survey: number; response: string; token?: string }
    >({
      query: (data) => ({
        url: '/survey/survey-response/',
        method: 'POST',
        body: data,
      }),
    }),

    // Get blog list with pagination
    getBlogList: builder.query<
      Array<{
        id: number;
        title: string;
        excerpt: string;
        content: string;
        alttext: string;
        featuredImage: string;
        delete: boolean;
        slug: string;
        tags: string[];
        publishedAt: string;
        status: number;
        seoTitle: string;
        seoDescription: string;
        author: {
          id: number;
          name: string;
        };
        categories: Array<{
          id: number;
          name: string;
          slug: string;
          postCount: number;
        }>;
      }>,
      { page?: number; pageSize?: number }
    >({
      query: ({ page = 1, pageSize = 9 }) => ({
        url: `/dashboard/vlogs/?page=${page}&page_size=${pageSize}`,
        method: 'GET',
      }),
    }),

    // Get blog details by slug
    getBlogDetails: builder.query<
      Array<{
        id: number;
        title: string;
        excerpt: string;
        content: string;
        alttext: string;
        featuredImage: string;
        delete: boolean;
        slug: string;
        tags: string[];
        publishedAt: string;
        status: number;
        seoTitle: string;
        seoDescription: string;
        author: {
          id: number;
          name: string;
        };
        categories: Array<{
          id: number;
          name: string;
          slug: string;
          postCount: number;
        }>;
      }>,
      string
    >({
      query: (slug) => ({
        url: `/dashboard/vlogs/${slug}`,
        method: 'GET',
      }),
    }),

    // Get blog categories
    getBlogCategories: builder.query<
      Array<{
        id: number;
        name: string;
        slug: string;
        postCount: number;
      }>,
      void
    >({
      query: () => ({
        url: '/dashboard/Category/',
        method: 'GET',
      }),
    }),

    // Get blogs by category
    getBlogsByCategory: builder.query<
      Array<{
        id: number;
        title: string;
        excerpt: string;
        content: string;
        alttext: string;
        featuredImage: string;
        delete: boolean;
        slug: string;
        tags: string[];
        publishedAt: string;
        status: number;
        seoTitle: string;
        seoDescription: string;
        author: {
          id: number;
          name: string;
        };
        categories: Array<{
          id: number;
          name: string;
          slug: string;
          postCount: number;
        }>;
      }>,
      { categorySlug: string; page?: number; pageSize?: number }
    >({
      query: ({ categorySlug, page = 1, pageSize = 9 }) => ({
        url: `/dashboard/vlogs/?category=${categorySlug}&page=${page}&page_size=${pageSize}`,
        method: 'GET',
      }),
    }),

    // Get latest blogs for homepage
    getLatestBlogs: builder.query<
      Array<{
        id: number;
        title: string;
        excerpt: string;
        content: string;
        alttext: string;
        featuredImage: string;
        delete: boolean;
        slug: string;
        tags: string[];
        publishedAt: string;
        status: number;
        seoTitle: string;
        seoDescription: string;
        author: {
          id: number;
          name: string;
        };
        categories: Array<{
          id: number;
          name: string;
          slug: string;
          postCount: number;
        }>;
      }>,
      { limit?: number }
    >({
      query: ({ limit = 2 }) => ({
        url: `/dashboard/vlogs/?page=1&page_size=${limit}`,
        method: 'GET',
      }),
    }),

    // Get all course questions with section details
    getAllCourseQuestions: builder.query<
      Array<{
        id: number;
        detail: {
          attempted_code?: string;
          words_per_minute?: number;
          attempt_count?: number;
          ai_usage?: boolean;
        };
        section: {
          id: number;
          created_at: string;
          updated_at: string;
          is_active: boolean;
          section_id: number;
          title: string;
          description: string;
          order: number;
          estimated_duration: string | null;
          course: number;
        };
        created_at: string;
        updated_at: string;
        is_active: boolean;
        question_id: number;
        prompt: string;
        explanation: string;
        tip: string;
        provided_code: string;
        expected_output: string;
        is_typing_course: boolean;
        ai_validation_required: boolean;
        order_in_section: number;
        is_final_activity: boolean;
        library: string;
        data: string;
        course: number;
        compare_mode: number;
      }>,
      number
    >({
      query: (courseId) => ({
        url: `/course/${courseId}/question`,
        method: 'GET',
      }),
    }),

    // Contact us form submission
    submitContactUs: builder.mutation<
      void,
      {
        email: string;
        name: string;
        message: string;
      }
    >({
      query: (contactData) => ({
        url: '/user/contactus/',
        method: 'POST',
        body: contactData,
      }),
    }),

    // Ping user activity
    pingUserActivity: builder.query<void, void>({
      query: () => ({
        url: '/user/active/ping/',
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useLogoutUserMutation,
  useRegisterUserMutation,
  useSubmitAffiliateFormMutation,
  useGetW9TaxFormMutation,
  useGetUserProfileQuery,
  useGetUserSubscriptionQuery,
  useCancelUserSubscriptionMutation,
  useVerifyUserMutation,
  useLoginUserMutation,
  useForgetPasswordMutation,
  useEmailVerifyMutation,
  useUpdateEmailMutation,
  useResetPasswordMutation,
  useGetAllCoursesQuery,
  useExecuteCodeMutation,
  useFetchCountriesQuery,
  useFetchStatesQuery,
  useFetchCitiesQuery,
  useCheckPromoCodeMutation,
  useSubmitTaxFormMutation,
  useCreateSetupIntentMutation,
  useActiveFreeTrialMutation,
  useRestartUserSubscriptionMutation,
  useSendSecurityEmailMutation,
  useGetCourseDetailsQuery,
  useGetNextCourseDetailsQuery,
  useGetCourseTopicsQuery,
  useGetSectionDetailsQuery,
  useGetSectionQuestionsQuery,
  useSubmitCourseAttemptMutation,
  useTrackCourseProgressMutation,
  useEnrollCourseMutation,
  useCompleteEnrollCourseMutation,
  useAiCodeCheckerMutation,
  useRetakeCourseMutation,
  useSaveWeeklyGoalMutation,
  useGetWeeklyGoalQuery,
  useGetCourseLanguageDistributionQuery,
  useGetCourseMedalsQuery,
  useGetCourseReportQuery,
  useGetDashboardGraphQuery,
  useGetCourseDetailedReportQuery,
  useGetSurveyQuestionsQuery,
  useSubmitSurveyResponseMutation,
  useGetBlogListQuery,
  useGetBlogDetailsQuery,
  useGetBlogCategoriesQuery,
  useGetBlogsByCategoryQuery,
  useGetLatestBlogsQuery,
  useGetAllCourseQuestionsQuery,
  useSubmitContactUsMutation,
  usePingUserActivityQuery,
  useLazyPingUserActivityQuery,
} = apiSlice;
